import {useCallback, useLayoutEffect, useRef} from 'react';

/**
 * Hook for determining if a component is mounted or not and performing actions when mounted
 * @example
 * ```tsx
 * const {act} = useComponentIsMounted();
 * useEffect(() => {
 * 	someAsyncAction().then(() => {
 * 		act(() => {
 * 			// do something
 * 		});
 * 	});
 * }, []);
 * ```
 */
export const useComponentIsMounted = () => {
	const componentIsMounted = useRef(false);
	useLayoutEffect(() => {
		componentIsMounted.current = true;
		return () => {
			componentIsMounted.current = false;
		};
	}, []);

	const shouldAct = useCallback(() => componentIsMounted.current === true, []);

	const act = useCallback(
		<T extends (...args: any[]) => any>(cb: T): undefined | ReturnType<T> => {
			if (!shouldAct()) return;
			return cb();
		},
		[],
	);

	return {
		act,
		componentIsMounted,
		shouldAct,
	};
};

export default useComponentIsMounted;
